import { Check } from 'lucide-react';
import {
  STARTER_STACK_VARIANTS,
  blockTypeMeta,
  type BlockType,
  type StarterStackVariantId,
} from '@pageboss/shared';
import { Modal } from './modal';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface StarterStackModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (variantId: StarterStackVariantId) => void;
  pendingVariantId?: StarterStackVariantId | null;
}

const BLOCK_LABELS: Record<BlockType, string> = Object.fromEntries(
  blockTypeMeta.map((m) => [m.type, m.label]),
) as Record<BlockType, string>;

export function StarterStackModal({
  open,
  onClose,
  onPick,
  pendingVariantId = null,
}: StarterStackModalProps) {
  const disabled = pendingVariantId !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Start with a starter stack"
      description="Pick a preset block composition closest to your business — you can rearrange or add blocks afterward."
      size="3xl"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={disabled}
            className="text-text-secondary"
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="flex h-full items-center">
        <div className="grid w-full grid-cols-1 gap-3">
        {STARTER_STACK_VARIANTS.map((variant) => {
          const isPending = pendingVariantId === variant.id;
          return (
            <button
              key={variant.id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(variant.id)}
              className={cn(
                'group relative flex flex-col gap-3 rounded-xl border border-border-subtle bg-card p-4 text-left transition-[border-color,box-shadow]',
                'hover:border-brand-500 hover:shadow-sm',
                'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isPending && 'border-brand-500 ring-2 ring-brand-500',
              )}
              aria-label={`Use ${variant.label} starter stack`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-base font-semibold tracking-tight text-foreground">
                  {variant.label}
                </div>
                {isPending ? (
                  <Check className="h-4 w-4 shrink-0 text-brand-700" aria-label="Selected" />
                ) : null}
              </div>
              <p className="text-sm leading-relaxed text-text-secondary">
                {variant.description}
              </p>
              <ol className="mt-auto flex flex-wrap gap-1.5 pt-2">
                {variant.blockTypes.map((type, i) => (
                  <li
                    key={`${type}-${i}`}
                    className="rounded-md bg-surface-sunken px-2 py-0.5 font-mono text-xs text-text-secondary"
                  >
                    {BLOCK_LABELS[type]}
                  </li>
                ))}
              </ol>
            </button>
          );
        })}
        </div>
      </div>
    </Modal>
  );
}
